import React, { useEffect, useMemo, useState } from "react";
import { Copy, Check, Building2, Landmark, Receipt, Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Confirmation screen shown to a guest who picked "Espèce" as a payment method.
 * Displays the 5-char `cash_code`, the office + bank instructions pulled from
 * settings, and a live countdown until `cash_expires_at`.
 *
 * Props:
 *   contribution: { cash_code, cash_expires_at, cash_window_hours, amount }
 *   eventCode:    str (used to build the reference shown to the guest)
 *   productName:  str (optional, shown above the code)
 *   onClose:      () => void
 */
export default function CashPaymentInstructions({ contribution, eventCode, productName, onClose }) {
  const [instr, setInstr] = useState(null);
  const [copied, setCopied] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/public/cash-instructions")
      .then((r) => setInstr(r.data))
      .catch(() => setInstr({}));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = useMemo(() => {
    if (!contribution?.cash_expires_at) return null;
    const ms = new Date(contribution.cash_expires_at).getTime() - now;
    if (ms <= 0) return { expired: true };
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return { expired: false, h, m, label: `${h}h ${m.toString().padStart(2, "0")}min` };
  }, [contribution, now]);

  const copy = (key, value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (!contribution) return null;
  const cashCode = contribution.cash_code || "—";
  const reference = `Cadeau ${cashCode}`;
  const windowH = contribution.cash_window_hours || instr?.window_hours || 72;

  return (
    <div
      data-testid="cash-instructions"
      className="space-y-4"
    >
      {/* Header */}
      <div className="text-center">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#3FA535] mb-2">
          Paiement en espèce
        </p>
        <h2 className="font-display text-2xl font-black text-[#2A0E3C]">
          Code de paiement généré
        </h2>
        {productName && (
          <p className="text-sm text-[#5C4664] mt-1">{productName}</p>
        )}
      </div>

      {/* Payment code */}
      <div className="bg-[#2A0E3C] rounded-2xl p-5 text-white text-center">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/70">
          Code de paiement
        </p>
        <div className="flex items-center justify-center gap-3 mt-2">
          <span
            data-testid="cash-code-value"
            className="font-display text-4xl font-black tracking-[0.3em] text-[#3FA535]"
          >
            {cashCode}
          </span>
          <button
            type="button"
            onClick={() => copy("code", cashCode)}
            data-testid="cash-code-copy"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Copier le code"
          >
            {copied === "code" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-white/80 mt-3">
          Référence à mentionner :{" "}
          <button
            type="button"
            onClick={() => copy("ref", reference)}
            data-testid="cash-ref-copy"
            className="font-mono font-bold text-white underline decoration-dotted hover:text-[#3FA535]"
          >
            « {reference} »
          </button>
          {copied === "ref" && <span className="ml-2 text-[#3FA535]">copié</span>}
        </p>
      </div>

      {/* Countdown */}
      {remaining && !remaining.expired && (
        <div
          data-testid="cash-countdown"
          className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-sm text-amber-800"
        >
          <Clock className="w-4 h-4 shrink-0" />
          <div className="flex-1">
            Vous disposez de <strong>{windowH}h</strong> pour effectuer le paiement.
            Temps restant : <strong>{remaining.label}</strong>.
          </div>
        </div>
      )}
      {remaining?.expired && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700">
          La fenêtre de paiement a expiré.
        </div>
      )}

      {/* Option A — Office */}
      <div className="bg-white border border-[#F2E2EC] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
            <Building2 className="w-4 h-4" />
          </div>
          <h3 className="font-display font-black text-[#2A0E3C]">
            Option A — Au bureau Milkado
          </h3>
        </div>
        {instr?.office_address ? (
          <>
            <p data-testid="cash-office-address" className="text-sm text-[#2A0E3C] whitespace-pre-line">
              {instr.office_address}
            </p>
            {instr?.office_hours && (
              <p className="text-xs text-[#5C4664] mt-1">
                Horaires : {instr.office_hours}
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-[#5C4664] italic">
            Adresse du bureau à communiquer prochainement — contactez le support.
          </p>
        )}
      </div>

      {/* Option B — Bank deposit */}
      <div className="bg-white border border-[#F2E2EC] rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl bg-[#EFE3F5] text-[#6B2C8F] flex items-center justify-center">
            <Landmark className="w-4 h-4" />
          </div>
          <h3 className="font-display font-black text-[#2A0E3C]">
            Option B — Dépôt bancaire
          </h3>
        </div>
        {instr?.bank_account ? (
          <ul className="text-sm space-y-1">
            {instr?.bank_name && (
              <li className="text-[#2A0E3C]">
                <span className="text-[#5C4664]">Banque :</span> <strong>{instr.bank_name}</strong>
              </li>
            )}
            {instr?.bank_beneficiary && (
              <li className="text-[#2A0E3C]">
                <span className="text-[#5C4664]">Bénéficiaire :</span> <strong>{instr.bank_beneficiary}</strong>
              </li>
            )}
            <li className="flex items-center gap-2">
              <span className="text-[#5C4664]">Compte :</span>
              <span data-testid="cash-bank-account" className="font-mono font-bold text-[#2A0E3C]">
                {instr.bank_account}
              </span>
              <button
                type="button"
                onClick={() => copy("acct", instr.bank_account)}
                data-testid="cash-bank-account-copy"
                className="ml-auto w-7 h-7 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] flex items-center justify-center text-[#5C4664]"
                aria-label="Copier le compte"
              >
                {copied === "acct" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </li>
            <li className="text-xs text-[#5C4664] mt-1 pt-1 border-t border-[#F2E2EC]">
              Indiquez impérativement la mention <strong>« {reference} »</strong> dans le motif du dépôt.
            </li>
          </ul>
        ) : (
          <p className="text-xs text-[#5C4664] italic">
            Coordonnées bancaires à communiquer prochainement — contactez le support.
          </p>
        )}
      </div>

      {/* Status note */}
      <div className="flex items-start gap-2 bg-[#FCE7F3] rounded-xl px-3 py-2 text-xs text-[#6B2C8F]">
        <Receipt className="w-4 h-4 shrink-0 mt-0.5" />
        <span>En attente de validation par l&apos;équipe Finances Milkado.</span>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          data-testid="cash-instructions-close"
          className="w-full h-12 rounded-full bg-[#2A0E3C] hover:bg-[#1A0729] text-white font-semibold"
        >
          Fermer
        </button>
      )}

      <p className="text-xs text-[#5C4664] mt-2">
        Montant à régler :{" "}
        <strong className="text-[#2A0E3C]">${(contribution.amount || 0).toFixed(2)}</strong>
        {" "}· Code événement : <span className="font-mono">{eventCode}</span>
      </p>
    </div>
  );
}
